/**
 * ExitTypeAnalysis — how trades exited.
 *   • Donut: share of trades by exit_reason.
 *   • Horizontal bars: average return (pnl_pct) per exit_reason.
 *
 * Props:
 *   data    from analytics.exitTypeStats(trades)
 *           [{reason, count, pct, winRate, avgPnlPct, totalPnl}]
 */

import { prettyReason } from '../lib/analytics';

const DONUT_COLORS = ['#0f766e', '#34d399', '#3b82f6', '#a855f7', '#f59e0b', '#ef4444', '#64748b', '#ec4899'];
const WIN = '#34d399';
const LOSS = '#f87171';

function polar(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arc(cx, cy, rOuter, rInner, startDeg, endDeg) {
  const so = polar(cx, cy, rOuter, endDeg);
  const eo = polar(cx, cy, rOuter, startDeg);
  const si = polar(cx, cy, rInner, startDeg);
  const ei = polar(cx, cy, rInner, endDeg);
  const large = endDeg - startDeg <= 180 ? 0 : 1;
  return [
    `M ${so.x.toFixed(2)} ${so.y.toFixed(2)}`,
    `A ${rOuter} ${rOuter} 0 ${large} 0 ${eo.x.toFixed(2)} ${eo.y.toFixed(2)}`,
    `L ${si.x.toFixed(2)} ${si.y.toFixed(2)}`,
    `A ${rInner} ${rInner} 0 ${large} 1 ${ei.x.toFixed(2)} ${ei.y.toFixed(2)}`,
    'Z',
  ].join(' ');
}

function money(v) {
  if (v == null) return '—';
  v = Math.abs(v)
  if (v >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)} Cr`;
  if (v >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(2)} L`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export default function ExitTypeAnalysis({ data = [] }) {
  if (!data.length) return null;

  const total = data.reduce((a, d) => a + d.count, 0);
  const colored = data.map((d, i) => ({ ...d, color: DONUT_COLORS[i % DONUT_COLORS.length] }));

  // Donut segments
  const cx = 90, cy = 90, rOuter = 80, rInner = 48;
  let cursor = 0;
  const segs = colored.map((d) => {
    const start = (cursor / total) * 360;
    cursor += d.count;
    const end = (cursor / total) * 360;
    return { ...d, start, end };
  });

  // total PNL bars: scale to max |totalPnl|
  const maxAbs = Math.max(1, ...colored.map((d) => Math.abs(d.totalPnl)));

  return (
    <div className="flex flex-col gap-5">
      {/* Donut + legend */}
      <div className="flex flex-wrap items-center gap-4">
        <svg viewBox="0 0 180 180" style={{ width: 150, height: 150, flexShrink: 0 }}>
          {segs.map((s) =>
            s.end - s.start > 0.01 ? (
              <path key={s.reason} d={arc(cx, cy, rOuter, rInner, s.start, s.end)} fill={s.color} stroke="#ffffff" strokeWidth="1" />
            ) : null
          )}
          <text x={cx} y={cy - 4} textAnchor="middle" fill="#333" fontSize="22" fontWeight="700">{total}</text>
          <text x={cx} y={cy + 14} textAnchor="middle" fill="#749094" fontSize="10">trades</text>
        </svg>

        <div className="flex flex-col gap-1.5 text-xs min-w-[140px] flex-1">
          {segs.map((s) => (
            <div key={s.reason} className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 truncate">
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                <span className="text-foreground truncate">{prettyReason(s.reason)}</span>
              </span>
              <span className="text-muted-foreground tabular-nums shrink-0">{s.pct.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Total PNL by exit type */}
      <div className="flex flex-col gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">PNL by Exit Type</h4>
        <div className="flex flex-col gap-2">
          {colored.map((d) => {
            const w = (Math.abs(d.totalPnl) / maxAbs) * 100;
            const pos = d.totalPnl >= 0;
            return (
              <div key={d.reason} className="flex items-center gap-2 text-xs">
                <span className="w-24 truncate text-muted-foreground shrink-0">{prettyReason(d.reason)}</span>
                <div className="flex-1 h-4 bg-surface-hover/40 rounded-sm overflow-hidden">
                  <div
                    className="h-full rounded-sm"
                    style={{ width: `${w.toFixed(1)}%`, background: pos ? WIN : LOSS }}
                  />
                </div>
                <span className={`w-14 text-right tabular-nums font-medium shrink-0 ${pos ? 'text-win' : 'text-loss'}`}>
                  {money(d.totalPnl)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
